import { useQueryClient } from '@tanstack/react-query';
import { useFocusEffect, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { Dimensions, FlatList, Platform, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import type { AdditionalDriver, Reservation } from '@/api/reservations/types';
import { useReservations } from '@/api/reservations/use-reservations';
import { useReservationsInCollection } from '@/api/reservations/use-reservations-in-collection';
import {
  additionalDriverStatus,
  findAdditionalDriver,
  isAdditionalDriverVerified,
} from '@/components/additional-drivers/status';
import Footer from '@/components/footer';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { TripsEmptyState } from '@/components/trips-empty-state';
import { formatNumber } from '@/core/utils';
import { Button, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Icon } from '@/ui/icon';
import { ArrowRight } from '@/ui/icons';
import { Warning } from '@/ui/icons/warning';
import { Loader } from '@/ui/loader';
import TripCard from '@/ui/trip-card';

const PAGE_WIDTH = Dimensions.get('window').width;

const LOCKED_TRIP_COPY = {
  background_check: {
    title: 'Reviewing Your Details',
    body: "We're reviewing your details. We'll let you know when you're approved to drive.",
  },
  insurance: {
    title: 'Waiting For Approval',
    body: 'You get access when the primary driver confirms you on their insurance.',
  },
};

// Where an additional driver lands when they open a trip: accept the invite,
// then the verify page while they still have steps to do, then a locked-trip
// dialog while they wait on review.
function additionalDriverTripAccess(driver?: AdditionalDriver) {
  if (!driver || isAdditionalDriverVerified(driver)) return 'details';
  if (additionalDriverStatus(driver) === 'invite_sent') return 'accept';
  if (!driver.setupComplete || driver.driversLicenseStatus !== 'approved') {
    return 'verify';
  }
  if (driver.backgroundCheckStatus !== 'approved') return 'background_check';
  if (driver.insuranceNeeded && driver.insuranceStatus !== 'approved') {
    return 'insurance';
  }
  return 'verify';
}

export default function Trips() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const [tab, setTab] = React.useState('active');
  const [refreshing, setRefreshing] = React.useState(false);
  const [lockedReason, setLockedReason] = React.useState<
    keyof typeof LOCKED_TRIP_COPY | null
  >(null);
  const queryClient = useQueryClient();

  const { data: customer } = useCustomer();

  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetched,
  } = useReservations({
    state: tab,
  });

  const { data: tripsInCollection, refetch: refetchTripsInCollections } =
    useReservationsInCollection();

  const lockedTrip = React.useMemo(
    () => tripsInCollection?.find((t) => !!t.lockedOutAt),
    [tripsInCollection]
  );

  const collectionTrip = React.useMemo(
    () => tripsInCollection?.find((t) => !t.lockedOutAt),
    [tripsInCollection]
  );

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  useEffect(() => {
    if (tab) {
      setRefreshing(true);

      resetInfiniteQueryPagination();
      refetch();

      setRefreshing(false);
    }
  }, [tab]);

  const resetInfiniteQueryPagination = (): void => {
    queryClient.setQueryData('reservations', (oldData) => {
      if (!oldData) return undefined;

      return {
        ...oldData,
        pages: oldData.pages.slice(0, 1),
        pageParams: oldData.pageParams.slice(0, 1),
      };
    });
  };

  const trips = React.useMemo(() => {
    return data?.pages.map((page) => page.data).flat();
  }, [data, tab]);

  const renderTripCard = (trip: Reservation) => {
    const access = additionalDriverTripAccess(
      findAdditionalDriver(trip.additionalDrivers, customer)
    );

    return (
      <TripCard
        trip={trip}
        invitedBy={
          access === 'accept' || access === 'verify'
            ? trip.customer.firstName
            : undefined
        }
        onPress={() => {
          if (access === 'details') {
            router.push(`/trips/${trip.id}/details`);
          } else if (access === 'accept') {
            router.push(`/trips/${trip.id}/accept-invite`);
          } else if (access === 'verify') {
            router.push(`/trips/${trip.id}/additional-drivers/verify`);
          } else {
            setLockedReason(access);
          }
        }}
        showChip={trip.customer.id === customer?.id}
      />
    );
  };

  const lockedTripModal = (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={1000}
      isVisible={!!lockedReason}
      hasBackdrop
      style={styles.lockedTripModalContainer}
      onBackdropPress={() => {
        setLockedReason(null);
      }}
    >
      <View style={styles.lockedTripModal}>
        <View style={styles.lockedTripModalHeader}>
          <Text type="title2">
            {lockedReason ? LOCKED_TRIP_COPY[lockedReason].title : ''}
          </Text>
          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setLockedReason(null);
            }}
          />
        </View>

        <Text type="bodyRegular">
          {lockedReason ? LOCKED_TRIP_COPY[lockedReason].body : ''}
        </Text>
      </View>
    </Modal>
  );

  if (Platform.OS === 'web') {
    return (
      <View style={styles.parentContainer}>
        {['xs', 'sm'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ flexDirection: 'row', padding: 16, gap: 16 }}>
            <Button
              tx="trips.activeTrips"
              type={tab === 'active' ? 'primarySmall' : 'secondarySmall'}
              onPress={() => {
                setTab('active');
              }}
              style={styles.tabButton}
            />

            <Button
              tx="trips.pastTrips"
              type={tab === 'completed' ? 'primarySmall' : 'secondarySmall'}
              onPress={() => {
                setTab('completed');
              }}
              style={styles.tabButton}
            />
          </View>
        ) : null}

        <FlatList
          style={styles.listContainer}
          contentContainerStyle={styles.listContentContainer}
          data={trips}
          renderItem={({ item }) => (
            <View style={styles.tripCardContainer}>{renderTripCard(item)}</View>
          )}
          ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          onEndReachedThreshold={0.5}
          onRefresh={() => {
            setRefreshing(true);

            resetInfiniteQueryPagination();
            refetch();

            setRefreshing(false);
          }}
          refreshing={refreshing}
          showsVerticalScrollIndicator={false}
          // eslint-disable-next-line react/no-unstable-nested-components
          ListHeaderComponent={() => {
            return (
              <View style={{ gap: 8 }}>
                {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
                  <View style={{ gap: 16, paddingBottom: 16 }}>
                    <NavigationButton
                      type={NavigationButtonTypes.back_with_text}
                      onPress={() => {
                        router.navigate('/');
                      }}
                    />

                    <Text type="largeTitle">Trips</Text>

                    <View style={{ flexDirection: 'row', gap: 16 }}>
                      <Button
                        tx="trips.activeTrips"
                        type={
                          tab === 'active' ? 'primarySmall' : 'secondarySmall'
                        }
                        onPress={() => {
                          setTab('active');
                        }}
                        style={styles.tabButton}
                      />

                      <Button
                        tx="trips.pastTrips"
                        type={
                          tab === 'completed'
                            ? 'primarySmall'
                            : 'secondarySmall'
                        }
                        onPress={() => {
                          setTab('completed');
                        }}
                        style={styles.tabButton}
                      />
                    </View>
                  </View>
                ) : null}

                {lockedTrip && (
                  <View>
                    <Alert
                      type={AlertType.ERROR}
                      title="Car locked"
                      body={`You have an outstanding balance of $${formatNumber(
                        lockedTrip.outstandingBalance ?? 0
                      )}. Pay now to restore access to your car.`}
                      icon={<Warning />}
                      buttonText="Pay balance"
                      onPress={() => {
                        router.replace(
                          `/account/trip-history/${lockedTrip.id}`
                        );
                      }}
                    />
                  </View>
                )}

                {collectionTrip && (
                  <View
                    style={{
                      padding: 16,
                      gap: 8,
                      backgroundColor: theme.colors.chip.error.backgroundColor,
                      borderRadius: 24,
                    }}
                  >
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <Icon
                        type="naked"
                        icon={<Warning />}
                        width={20}
                        height={20}
                      />

                      <Text type="bodyRegular">
                        You Have an Outstanding Balance
                      </Text>
                    </View>

                    <View>
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Your overdue balance needs to be settled by{' '}
                        <Text type="caption1">
                          {(collectionTrip.lockoutDatetime
                            ? DateTime.fromISO(collectionTrip.lockoutDatetime)
                            : DateTime.fromISO(
                                collectionTrip.sentToCollectionsAt
                              ).plus({ days: 3 })
                          ).toFormat('MM-dd-yyyy hh:mm a ZZZZ')}
                          .{' '}
                        </Text>
                        Otherwise, you will be locked out of any current rental
                        and your account will be sent to collections.{' '}
                        <Text type="caption1">
                          Please settle promptly to avoid disruption.
                        </Text>
                      </Text>
                    </View>

                    <View>
                      <Button
                        text="Pay balance"
                        type="primaryExtraSmall"
                        onPress={() => {
                          router.replace(
                            `/account/trip-history/${collectionTrip.id}`
                          );
                        }}
                        rightIcon={
                          <Icon
                            type="naked"
                            icon={<ArrowRight />}
                            fillColor={theme.colors.button.primary.default.text}
                            width={10}
                            height={10}
                          />
                        }
                        style={{ width: 120 }}
                      />
                    </View>
                  </View>
                )}

                {isFetching && !isFetchingNextPage ? (
                  <View
                    style={{
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      paddingBottom: 16,
                    }}
                  >
                    <Loader style={{ height: 150, width: 150 }} />
                  </View>
                ) : null}
              </View>
            );
          }}
          ListHeaderComponentStyle={styles.listHeader}
          // eslint-disable-next-line react/no-unstable-nested-components
          ListEmptyComponent={() => {
            if (!isFetched) return null;

            if (isFetching) return null;

            return (
              <TripsEmptyState
                onPress={() => {
                  router.navigate('/search');
                }}
              />
            );
          }}
          // eslint-disable-next-line react/no-unstable-nested-components
          ListFooterComponent={() => {
            // if (!isFetchingNextPage) return <Footer />;

            return (
              <View style={{ paddingTop: 200 }}>
                {isFetchingNextPage ? (
                  <View style={{ width: '100%', alignItems: 'center' }}>
                    <Loader style={styles.loader} />
                  </View>
                ) : null}

                <Footer />
              </View>
            );
          }}
          ListFooterComponentStyle={{
            justifyContent: 'flex-end',
            width: PAGE_WIDTH,
          }}
        />

        {lockedTripModal}
      </View>
    );
  }

  return (
    <View
      style={{
        flexGrow: 1,
        backgroundColor: theme.colors.background_1,
        gap: 16,
        paddingTop: {
          md: 56,
        },
        alignItems: 'center',
      }}
    >
      <View
        style={{
          flexGrow: 1,
          padding: 16,
          gap: 16,
        }}
      >
        <View style={{ flexDirection: 'row', gap: 16 }}>
          <Button
            tx="trips.activeTrips"
            type={tab === 'active' ? 'primarySmall' : 'secondarySmall'}
            onPress={() => {
              setTab('active');
            }}
            style={styles.tabButton}
          />

          <Button
            tx="trips.pastTrips"
            type={tab === 'completed' ? 'primarySmall' : 'secondarySmall'}
            onPress={() => {
              setTab('completed');
            }}
            style={styles.tabButton}
          />
        </View>

        <View style={{ flexGrow: 1, height: '100%' }}>
          <FlatList
            contentContainerStyle={{ paddingBottom: 200, flexGrow: 1 }}
            data={trips}
            renderItem={({ item }) => renderTripCard(item)}
            ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
            onEndReached={() => {
              if (hasNextPage && !isFetchingNextPage) {
                console.log('end reached');
                fetchNextPage();
              }
            }}
            onEndReachedThreshold={0.5}
            onRefresh={() => {
              setRefreshing(true);

              resetInfiniteQueryPagination();
              refetch();
              refetchTripsInCollections();

              setRefreshing(false);
            }}
            refreshing={refreshing}
            showsVerticalScrollIndicator={false}
            // eslint-disable-next-line react/no-unstable-nested-components
            ListHeaderComponent={() => {
              return (
                <View
                  style={{ flexDirection: 'column', gap: 8, paddingBottom: 16 }}
                >
                  {lockedTrip && (
                    <View>
                      <Alert
                        type={AlertType.ERROR}
                        title="Car locked"
                        body={`You have an outstanding balance of $${formatNumber(
                          lockedTrip.outstandingBalance ?? 0
                        )}. Pay now to restore access to your car.`}
                        icon={<Warning />}
                        buttonText="Pay balance"
                        onPress={() => {
                          router.replace(
                            `/account/trip-history/${lockedTrip.id}`
                          );
                        }}
                      />
                    </View>
                  )}

                  {collectionTrip && (
                    <View
                      style={{
                        padding: 16,
                        backgroundColor:
                          theme.colors.chip.error.backgroundColor,
                        borderRadius: 24,
                        gap: 8,
                      }}
                    >
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <Icon
                          type="naked"
                          icon={<Warning />}
                          width={20}
                          height={20}
                        />

                        <Text type="bodyRegular">
                          You Have an Outstanding Balance
                        </Text>
                      </View>

                      <View>
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Your overdue balance needs to be settled by{' '}
                          <Text type="caption1">
                            {(collectionTrip.lockoutDatetime
                              ? DateTime.fromISO(collectionTrip.lockoutDatetime)
                              : DateTime.fromISO(
                                  collectionTrip.sentToCollectionsAt
                                ).plus({ days: 3 })
                            ).toFormat('MM-dd-yyyy hh:mm a ZZZZ')}
                            .{' '}
                          </Text>
                          Otherwise, you will be locked out of any current
                          rental and your account will be sent to collections.{' '}
                          <Text type="caption1">
                            Please settle promptly to avoid disruption.
                          </Text>
                        </Text>
                      </View>
                      <View>
                        <Button
                          text="Pay balance"
                          type="primaryExtraSmall"
                          onPress={() => {
                            router.replace(
                              `/account/trip-history/${collectionTrip.id}`
                            );
                          }}
                          rightIcon={
                            <Icon
                              type="naked"
                              icon={<ArrowRight />}
                              fillColor={
                                theme.colors.button.primary.default.text
                              }
                              width={10}
                              height={10}
                            />
                          }
                          style={{ width: 120 }}
                        />
                      </View>
                    </View>
                  )}

                  {isFetching && !isFetchingNextPage ? (
                    <View
                      style={{
                        width: '100%',
                        height: trips?.length > 0 ? 'inherit' : '100%',
                        alignItems: 'center',
                      }}
                    >
                      <Loader style={{ height: 150, width: 150 }} />
                    </View>
                  ) : null}
                </View>
              );
            }}
            ListHeaderComponentStyle={styles.listHeader}
            // eslint-disable-next-line react/no-unstable-nested-components
            ListEmptyComponent={() => {
              if (!isFetched) return null;

              if (isFetching) return null;

              return (
                <TripsEmptyState
                  onPress={() => {
                    router.navigate('/search');
                  }}
                />
              );
            }}
            // eslint-disable-next-line react/no-unstable-nested-components
            ListFooterComponent={() => {
              // if (!isFetchingNextPage) return <Footer />;

              return (
                <View>
                  {isFetchingNextPage ? (
                    <View style={{ width: '100%', alignItems: 'center' }}>
                      <Loader style={styles.loader} />
                    </View>
                  ) : null}

                  <Footer />
                </View>
              );
            }}
            ListFooterComponentStyle={{
              justifyContent: 'flex-end',
              width: PAGE_WIDTH,
            }}
          />
        </View>
      </View>

      {lockedTripModal}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  parentContainer: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    paddingTop: {
      md: 56,
    },
  },
  listHeader: {
    width: {
      xs: '100%',
      md: 600,
    },
  },
  listContainer: {
    flexGrow: 1,
  },
  listContentContainer: {
    flexGrow: 1,
    padding: 16,
    paddingTop: {
      xs: 0,
      md: 16,
    },
    width: {
      xs: PAGE_WIDTH,
      md: 600,
    },
    gap: 16,
    alignSelf: 'center',
    alignItems: 'center',
  },
  tripCardContainer: {
    // flexGrow: 1,
    width: {
      xs: PAGE_WIDTH - 32,
      md: 600,
    },
  },
  loader: {
    width: {
      md: 100,
    },
    height: {
      md: 100,
    },
  },
  tabButton: {
    flex: 1,
    width: {
      md: 150,
    },
  },
  lockedTripModalContainer: {
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 700,
    },
  },
  lockedTripModal: {
    width: '90%',
    backgroundColor: theme.colors.background_2,
    borderRadius: 16,
    padding: 16,
    gap: 16,
  },
  lockedTripModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));
